import React, { useState, useEffect } from 'react';
import { IndianRupee, Upload, Calendar, CreditCard, FileText, Download, X, Send, Trash2, Ban } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentTrackingSection = ({ invoice, onPaymentRecorded }) => {
  const { getAuthHeader, admin } = useAuth();
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancellingPayment, setCancellingPayment] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelPaymentIndex, setCancelPaymentIndex] = useState(null);
  const [cancelPaymentAmount, setCancelPaymentAmount] = useState(0);
  const [cancelReason, setCancelReason] = useState('');
  
  const [paymentData, setPaymentData] = useState({
    amount: '',
    discount_type: 'none',
    discount_value: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Bank Transfer',
    transaction_reference: '',
    notes: ''
  });
  
  const [screenshots, setScreenshots] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState([]);

  const paymentMethods = [
    'Bank Transfer',
    'Cash',
    'UPI',
    'Credit Card',
    'Debit Card',
    'Online Payment',
    'Cheque'
  ];

  // Process consultant is now set during invoice creation, not during payment
  // Removed fetchProcessConsultants - no longer needed here


  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length + screenshots.length > 5) {
      alert('Maximum 5 screenshots allowed');
      return;
    }
    
    // Upload each screenshot
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(
          `${API}/invoices/${invoice.id}/upload-payment-screenshot`,
          formData,
          {
            headers: {
              ...getAuthHeader(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        setUploadedUrls(prev => [...prev, response.data.screenshot_url]);
        setScreenshots(prev => [...prev, {
          name: file.name,
          url: response.data.screenshot_url
        }]);
      } catch (error) {
        console.error('Failed to upload screenshot:', error);
        alert(`Failed to upload ${file.name}`);
      }
    }
  };

  const handleRemoveScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
    setUploadedUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(paymentData.amount);
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    // Calculate discount
    let discountAmount = 0;
    const discountValue = parseFloat(paymentData.discount_value) || 0;
    
    if (paymentData.discount_type === 'percentage') {
      discountAmount = (amount * discountValue) / 100;
    } else if (paymentData.discount_type === 'fixed') {
      discountAmount = discountValue;
    }
    
    const netAmount = amount - discountAmount;
    
    if (discountAmount >= amount) {
      alert('Discount cannot be equal to or greater than payment amount');
      return;
    }
    
    const roundedDue = Math.round(invoice.amount_due || 0);
    if (Math.round(netAmount) > roundedDue) {
      alert(`Net payment amount (₹${Math.round(netAmount).toLocaleString()}) cannot exceed due amount (₹${roundedDue.toLocaleString()})`);
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        ...paymentData,
        amount: Math.round(amount),
        discount_value: discountValue,
        screenshot_files: uploadedUrls
      };
      
      const response = await axios.post(
        `${API}/invoices/${invoice.id}/confirm-payment`,
        payload,
        { headers: getAuthHeader() }
      );
      
      alert(response.data.message);
      
      // Reset form
      setPaymentData({
        amount: '',
        discount_type: 'none',
        discount_value: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Bank Transfer',
        transaction_reference: '',
        notes: ''
      });
      setScreenshots([]);
      setUploadedUrls([]);
      setShowPaymentForm(false);
      
      onPaymentRecorded && onPaymentRecorded();
    } catch (error) {
      console.error('Failed to record payment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to record payment';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Open cancel payment modal
  const openCancelModal = (paymentIndex, paymentAmount) => {
    setCancelPaymentIndex(paymentIndex);
    setCancelPaymentAmount(paymentAmount);
    setCancelReason('');
    setShowCancelModal(true);
  };

  // Cancel payment record (Admin/SuperAdmin)
  const handleCancelPayment = async () => {
    if (!cancelReason.trim()) {
      alert('Please enter a reason for cancellation');
      return;
    }
    
    setCancellingPayment(cancelPaymentIndex);
    try {
      const response = await axios.post(
        `${API}/invoices/${invoice.id}/payment/${cancelPaymentIndex}/cancel`,
        { reason: cancelReason },
        { headers: getAuthHeader() }
      );
      alert(response.data.message);
      setShowCancelModal(false);
      setCancelPaymentIndex(null);
      setCancelReason('');
      onPaymentRecorded && onPaymentRecorded();
    } catch (error) {
      console.error('Failed to cancel payment:', error);
      alert(error.response?.data?.detail || 'Failed to cancel payment');
    } finally {
      setCancellingPayment(null);
    }
  };

  // Delete payment record (SuperAdmin only)
  const handleDeletePayment = async (paymentIndex, paymentAmount) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE this payment of ₹${Math.round(paymentAmount).toLocaleString()}? This action cannot be undone.`)) {
      return;
    }
    
    setDeletingPayment(paymentIndex);
    try {
      const response = await axios.delete(
        `${API}/invoices/${invoice.id}/payment/${paymentIndex}`,
        { headers: getAuthHeader() }
      );
      alert(response.data.message);
      onPaymentRecorded && onPaymentRecorded();
    } catch (error) {
      console.error('Failed to delete payment:', error);
      alert(error.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setDeletingPayment(null);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const colors = {
      pending: { bg: '#fef3c7', text: '#92400e', label: 'Pending' },
      partial: { bg: '#dbeafe', text: '#1e40af', label: 'Partial' },
      paid: { bg: '#d1fae5', text: '#065f46', label: 'Paid' }
    };
    
    const color = colors[status] || colors.pending;
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        backgroundColor: color.bg,
        color: color.text
      }}>
        {color.label}
      </span>
    );
  };

  return (
    <div style={{
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      padding: '20px',
      marginTop: '20px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '15px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <IndianRupee size={20} style={{ color: '#777db0' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
            Payment Tracking
          </h3>
          {getPaymentStatusBadge(invoice.payment_status)}
        </div>
        
        {hasPermission(admin, 'record_payments') && invoice.payment_status !== 'paid' && !showPaymentForm && (
          <button
            className="btn-primary"
            onClick={() => setShowPaymentForm(true)}
            style={{ fontSize: '13px', padding: '6px 12px' }}
          >
            + Record Payment
          </button>
        )}
      </div>

      {/* Payment Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '15px',
        marginBottom: '20px',
        padding: '15px',
        background: '#f9fafb',
        borderRadius: '8px'
      }}>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
            Total Amount
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
            ₹{Math.round(invoice.total_amount || 0).toLocaleString()}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
            Amount Paid
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>
            ₹{Math.round(invoice.amount_paid || 0).toLocaleString()}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
            Balance Due
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>
            ₹{Math.round(invoice.amount_due || 0).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Payment Form */}
      {showPaymentForm && (
        <form onSubmit={handleSubmit} style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px',
          background: '#ffffff'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>
              Record New Payment
            </h4>
            <button
              type="button"
              onClick={() => setShowPaymentForm(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X size={18} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label className="form-label">
                <IndianRupee size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Gross Amount (₹) *
              </label>
              <input
                type="number"
                step="0.01"
                className="form-input"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                placeholder={`Max: ₹${invoice.amount_due?.toFixed(2)}`}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Payment Date *
              </label>
              <input
                type="date"
                className="form-input"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                On Payment Discount Type
              </label>
              <select
                className="form-input"
                value={paymentData.discount_type}
                onChange={(e) => {
                  setPaymentData({ ...paymentData, discount_type: e.target.value, discount_value: e.target.value === 'none' ? '' : paymentData.discount_value });
                }}
              >
                <option value="none">No Discount</option>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (₹)</option>
              </select>
            </div>

            {paymentData.discount_type !== 'none' && (
              <div className="form-group">
                <label className="form-label">
                  Discount Value {paymentData.discount_type === 'percentage' ? '(%)' : '(₹)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={paymentData.discount_value}
                  onChange={(e) => setPaymentData({ ...paymentData, discount_value: e.target.value })}
                  placeholder={paymentData.discount_type === 'percentage' ? 'e.g., 5 for 5%' : 'e.g., 1000'}
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                <CreditCard size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Payment Method *
              </label>
              <select
                className="form-input"
                value={paymentData.payment_method}
                onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                required
              >
                {paymentMethods.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FileText size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Transaction Reference
              </label>
              <input
                type="text"
                className="form-input"
                value={paymentData.transaction_reference}
                onChange={(e) => setPaymentData({ ...paymentData, transaction_reference: e.target.value })}
                placeholder="UTR, Cheque No, etc."
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Payment Screenshots (Max 5)</label>
            <input
              type="file"
              accept="image/*,.pdf"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="payment-screenshot-upload"
            />
            <label
              htmlFor="payment-screenshot-upload"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                border: '1px dashed #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#6b7280'
              }}
            >
              <Upload size={16} />
              Upload Screenshots
            </label>
            
            {screenshots.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {screenshots.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 10px',
                      background: '#f3f4f6',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  >
                    <FileText size={14} />
                    <span>{file.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveScreenshot(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        color: '#ef4444'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-input"
              value={paymentData.notes}
              onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
              rows={2}
              placeholder="Any additional notes..."
            />
          </div>

          {/* Net Amount Display */}
          {paymentData.amount && paymentData.discount_type !== 'none' && paymentData.discount_value && (
            <div style={{
              padding: '12px',
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '6px',
              marginTop: '10px'
            }}>
              <div style={{ fontSize: '13px', color: '#166534', marginBottom: '4px' }}>
                <strong>Payment Breakdown:</strong>
              </div>
              <div style={{ fontSize: '12px', color: '#166534' }}>
                Gross Amount: ₹{parseFloat(paymentData.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <br />
                Discount ({paymentData.discount_type === 'percentage' ? `${paymentData.discount_value}%` : `₹${paymentData.discount_value}`}): 
                - ₹{(paymentData.discount_type === 'percentage' 
                  ? (parseFloat(paymentData.amount) * parseFloat(paymentData.discount_value)) / 100
                  : parseFloat(paymentData.discount_value)
                ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <br />
                <strong>Net Payment Amount: ₹{(
                  parseFloat(paymentData.amount) - (
                    paymentData.discount_type === 'percentage'
                      ? (parseFloat(paymentData.amount) * parseFloat(paymentData.discount_value)) / 100
                      : parseFloat(paymentData.discount_value)
                  )
                ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '15px' }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowPaymentForm(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Recording...' : 'Confirm Payment'}
            </button>
          </div>
        </form>
      )}

      {/* Payment History */}
      {invoice.payment_records && invoice.payment_records.length > 0 && (
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>
            Payment History
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {invoice.payment_records.map((payment, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  background: payment.status === 'cancelled' ? '#fef2f2' : '#f9fafb',
                  borderRadius: '6px',
                  border: payment.status === 'cancelled' ? '1px solid #fecaca' : '1px solid #e5e7eb',
                  opacity: payment.status === 'cancelled' ? 0.8 : 1
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ 
                      fontWeight: '600', 
                      color: payment.status === 'cancelled' ? '#9ca3af' : '#111827',
                      textDecoration: payment.status === 'cancelled' ? 'line-through' : 'none'
                    }}>
                      ₹{Math.round(payment.amount || 0).toLocaleString()}
                    </span>
                    {payment.status === 'cancelled' && (
                      <span style={{
                        padding: '2px 6px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        CANCELLED
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {payment.payment_date}
                    </span>
                    
                    {/* Receipt buttons - only for active payments */}
                    {payment.status !== 'cancelled' && hasPermission(admin, 'download_payment_receipts') && (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/invoices/${invoice.id}/payment-receipt/${index}/download-pdf`, {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                              }
                            });
                            
                            if (response.ok) {
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Receipt_${invoice.billing_name?.replace(' ', '_')}_${payment.payment_date?.replace(/-/g, '')}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } else {
                              // Parse error response
                              const errorData = await response.json().catch(() => ({}));
                              alert(errorData.detail || 'Failed to download payment receipt');
                            }
                          } catch (error) {
                            console.error('Download error:', error);
                            alert('Failed to download payment receipt');
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Download Receipt"
                      >
                        <Download size={12} />
                        Receipt
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Send payment receipt for invoice ${invoice.invoice_number} to the client via email?`)) {
                            return;
                          }
                          try {
                            const response = await axios.post(
                              `${BACKEND_URL}/api/invoices/${invoice.id}/payment-receipt/${index}/send`,
                              {},
                              { headers: getAuthHeader() }
                            );
                            
                            if (response.status === 200) {
                              alert(response.data?.message || 'Payment receipt sent successfully');
                              onPaymentRecorded();
                            }
                          } catch (error) {
                            console.error('Error sending payment receipt:', error);
                            alert(error.response?.data?.detail || 'Failed to send payment receipt. Please ensure the receipt PDF has been generated.');
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          background: payment.receipt_sent ? '#6366f1' : '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title={payment.receipt_sent ? "Re-Send Receipt" : "Send Receipt"}
                      >
                        <Send size={12} />
                        {payment.receipt_sent ? 'Re-Send' : 'Send'}
                      </button>
                    </>
                    )}
                    
                    {/* Cancel Payment Button - Admin/SuperAdmin, only for active payments */}
                    {payment.status !== 'cancelled' && (admin?.role === 'admin' || admin?.role === 'super_admin') && (
                      <button
                        onClick={() => openCancelModal(index, payment.amount)}
                        disabled={cancellingPayment === index}
                        style={{
                          padding: '4px 8px',
                          background: '#f97316',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '11px',
                          cursor: cancellingPayment === index ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          opacity: cancellingPayment === index ? 0.7 : 1
                        }}
                        title="Cancel Payment"
                      >
                        <Ban size={12} />
                        {cancellingPayment === index ? '...' : 'Cancel'}
                      </button>
                    )}
                    
                    {/* Delete Payment Button - SuperAdmin only (always visible for cancelled, only for super_admin for active) */}
                    {(payment.status === 'cancelled' || admin?.role === 'super_admin') && admin?.role === 'super_admin' && (
                      <button
                        onClick={() => handleDeletePayment(index, payment.amount)}
                        disabled={deletingPayment === index}
                        style={{
                          padding: '4px 8px',
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '11px',
                          cursor: deletingPayment === index ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          opacity: deletingPayment === index ? 0.7 : 1
                        }}
                        title="Delete Payment Permanently"
                      >
                        <Trash2 size={12} />
                        {deletingPayment === index ? '...' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: payment.status === 'cancelled' ? '#9ca3af' : '#6b7280' }}>
                  <div><strong>Method:</strong> {payment.payment_method}</div>
                  {payment.transaction_reference && (
                    <div><strong>Ref:</strong> {payment.transaction_reference}</div>
                  )}
                  {payment.notes && <div><strong>Notes:</strong> {payment.notes}</div>}
                  {payment.recorded_by && (
                    <div style={{ fontSize: '11px', marginTop: '4px' }}>
                      Recorded by {payment.recorded_by}
                    </div>
                  )}
                  {/* Show cancellation info */}
                  {payment.status === 'cancelled' && (
                    <div style={{ 
                      fontSize: '11px', 
                      marginTop: '6px', 
                      padding: '6px',
                      background: '#fee2e2',
                      borderRadius: '4px',
                      color: '#b91c1c'
                    }}>
                      <strong>Cancelled</strong> by {payment.cancelled_by} on {new Date(payment.cancelled_at).toLocaleDateString()}
                      {payment.cancel_reason && <div>Reason: {payment.cancel_reason}</div>}
                    </div>
                  )}
                  {payment.screenshot_urls && payment.screenshot_urls.length > 0 && (
                    <div style={{ marginTop: '6px' }}>
                      <strong>Screenshots:</strong>
                      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                        {payment.screenshot_urls.map((url, idx) => (
                          <a
                            key={idx}
                            href={`${BACKEND_URL}${url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              background: '#ffffff',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '11px',
                              color: '#777db0',
                              textDecoration: 'none'
                            }}
                          >
                            <Download size={12} />
                            View
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancel Payment Modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '420px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#fef3c7'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '600',
                color: '#92400e',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Ban size={18} />
                Cancel Payment
              </h3>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelPaymentIndex(null);
                  setCancelReason('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px' }}>
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
                  <strong>Warning:</strong> Cancelling this payment will update the invoice balance. 
                  The payment record will be marked as cancelled but not deleted.
                </p>
              </div>

              <div style={{
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '13px' }}>Payment Amount:</span>
                  <span style={{ fontWeight: '600', color: '#111827' }}>
                    ₹{Math.round(cancelPaymentAmount).toLocaleString()}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280', fontSize: '13px' }}>Invoice:</span>
                  <span style={{ fontWeight: '600', color: '#111827' }}>
                    {invoice.invoice_number}
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Reason for Cancellation <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason for cancelling this payment..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelPaymentIndex(null);
                    setCancelReason('');
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
                <button
                  onClick={handleCancelPayment}
                  disabled={!cancelReason.trim() || cancellingPayment !== null}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    backgroundColor: !cancelReason.trim() ? '#fcd34d' : '#f97316',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: !cancelReason.trim() ? 'not-allowed' : 'pointer',
                    opacity: !cancelReason.trim() ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Ban size={16} />
                  {cancellingPayment !== null ? 'Cancelling...' : 'Cancel Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentTrackingSection;
